/**
 * Macros simples (as "Macros Simples" do módulo embutido do original): um nome e
 * linhas que rodam como se fossem digitadas no chat, uma por vez ("/r 1d20+5",
 * "/me ataca", texto…). Chamadas por `/nome` no chat.
 *
 * Três lugares, nesta ordem de busca:
 * 1. da mesa, para todos: documento NDB do item `macros` da sala (só mestres mudam);
 * 2. minhas, desta mesa;
 * 3. minhas, globais.
 * As minhas ficam neste navegador, como no original (que as guarda no disco local).
 */
import { useEffect, useState } from 'react';
import type { NodeTree, Op } from '../../ndb.js';

export type Macro = { id: string; name: string; lines: string };

/** Nome sem espaços nem "/" (o original pede o mesmo). */
export const MACRO_NAME = /^[^\s/]{1,30}$/;
export const MAX_LINES = 20;

export const macroLines = (m: Macro): string[] =>
  m.lines
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean)
    .slice(0, MAX_LINES);

const fold = (s: string) =>
  s
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase();

/**
 * As listas sem as macros que uma lista anterior já tem com o mesmo nome (sem diferenciar maiúsculas
 * nem acentos, como a busca do chat): o botão de cada nome roda a macro que o `/nome` rodaria.
 */
export function distinctMacros(...lists: Macro[][]): Macro[][] {
  const taken = new Set<string>();
  return lists.map((list) =>
    list.filter((m) => {
      const key = fold(m.name);
      if (taken.has(key)) return false;
      taken.add(key);
      return true;
    }),
  );
}

/** A primeira macro com o nome, na ordem das listas dadas. */
export function findMacro(name: string, ...lists: Macro[][]): Macro | null {
  const key = fold(name);
  for (const list of lists) {
    const hit = list.find((m) => fold(m.name) === key);
    if (hit) return hit;
  }
  return null;
}

/* ------------------------------------------------------------------ macros da mesa (NDB) */

export function readRoomMacros(tree: NodeTree): Macro[] {
  return tree.root.children
    .filter((c) => c.name === 'macro')
    .map((c) => ({ id: c.id, name: String(c.attrs.get('name') ?? ''), lines: String(c.attrs.get('lines') ?? '') }))
    .filter((m) => m.name);
}

export function addRoomMacroOps(rootId: string, id: string, m: Omit<Macro, 'id'>): Op[] {
  return [
    { t: 'add', parent: rootId, id, name: 'macro' },
    { t: 'set', node: id, name: 'name', value: m.name },
    { t: 'set', node: id, name: 'lines', value: m.lines },
  ];
}

export function updateRoomMacroOps(m: Macro): Op[] {
  return [
    { t: 'set', node: m.id, name: 'name', value: m.name },
    { t: 'set', node: m.id, name: 'lines', value: m.lines },
  ];
}

/* ------------------------------------------------------------------ minhas (neste navegador) */

export type MyMacros = { global: Macro[]; rooms: Record<string, Macro[]> };

const key = (userId: string) => `rpgdesk.macros.${userId}`;
const empty = (): MyMacros => ({ global: [], rooms: {} });

export function loadMyMacros(userId: string): MyMacros {
  try {
    const raw = window.localStorage.getItem(key(userId));
    if (!raw) return empty();
    const parsed = JSON.parse(raw) as Partial<MyMacros>;
    return { global: Array.isArray(parsed.global) ? parsed.global : [], rooms: parsed.rooms && typeof parsed.rooms === 'object' ? parsed.rooms : {} };
  } catch {
    return empty();
  }
}

const listeners = new Set<() => void>();

export function saveMyMacros(userId: string, mine: MyMacros): void {
  try {
    window.localStorage.setItem(key(userId), JSON.stringify(mine));
  } catch {
    // sem armazenamento: as macros valem até fechar a página
  }
  for (const l of listeners) l();
}

/** As minhas macros, atualizadas quando mudam (nesta aba ou em outra). */
export function useMyMacros(userId: string | undefined): MyMacros {
  const [mine, setMine] = useState<MyMacros>(() => (userId ? loadMyMacros(userId) : empty()));
  useEffect(() => {
    if (!userId) return;
    const reload = () => setMine(loadMyMacros(userId));
    reload();
    listeners.add(reload);
    const onStorage = (e: StorageEvent) => e.key === key(userId) && reload();
    window.addEventListener('storage', onStorage);
    return () => {
      listeners.delete(reload);
      window.removeEventListener('storage', onStorage);
    };
  }, [userId]);
  return mine;
}
